import { createSlice } from "@reduxjs/toolkit";
import FILTERS_KEYS from "@utils/filters_keys.js";
import { REHYDRATE } from "redux-persist/es/constants";
import { mergeFilters, toggleReduxSliceFiltersVisibility } from "../../helpers/reduxHelpers.js";

const defaultFilters = [
	{ id: FILTERS_KEYS.vessels.clients, name: "vessels:clientsFilter", visible: false },
	{ id: FILTERS_KEYS.vessels.distributors, name: "vessels:distributorsFilter", visible: false },
	{ id: FILTERS_KEYS.vessels.businessUnits, name: "vessels:businessUnitFilter", visible: false },
	{ id: FILTERS_KEYS.vessels.status, name: "vessels:statusFilter", visible: false },
];
const initialState = {
	data: null,
	loading: false,
	listModules: [],
	list: [],
	filters: [...defaultFilters],

	pagination: {
		page: 1,
		perPage: 10,
		totalItems: 0,
		totalPage: 0,
	},
	selectedVessel: null,
	vesselDetails: null,
	starlink: {
		loadingVesselDetails: false,
		loadingVesselDetailsStarlink: false,
		hasStarlink: false,
		starlinkUsage: [],
		starlinkAlerts: [],
		starlinkTelemetry: [],
	},
	peplink: {
		loading: false,
		hasPeplink: false,
		wan1: [],
		wan2: [],
		cellular: [],
		quality: [],
		status: [],
		stats: {
			min_up: 0,
			max_up: 0,
			min_down: 0,
			max_down: 0,
			last_down: 0,
			last_up: 0,
			allowance_gb: 0,
		},
		subscription: [],
		oraganization: [],
		group: [],
	},
	intelsat: {
		loading: false,
		hasIntelsat: false,
		data: null,
	},
	cybersecurity: {
		loading: false,
		hasCybersecurity: false,
		data: null,
	},
	activation: {
		kits: {
			loadingkits: false,
			data: null,
			pagination: {
				page: 1,
				perPage: 10,
				totalItems: 0,
				totalPage: 0,
			},
			history: {
				data: [],
				loading: false,
				pagination: {
					totalItems: 0,
					page: 1,
					perPage: 10,
					totalPage: 0,
				},
			},
		},
		selectedKit: {
			id: null,
			name: null,
			data: null,
			loading: false,
		},
	},
	fortigate: {
		loading: false,
		hasFortigate: false,
		pingHighLatency: [],
		starlinkFortigateInterfaceCorporate: [],
		starlinkFortigateInterfaceWelfare: [],
		starlinkFortigateInterfaceWAN1: [],
		starlinkFortigateInterfaceWAN2: [],
	},
	yahclick: {
		loading: false,
		hasYahclick: false,
		yahclickData: [],
		pingHighLatencyYahclick: [],
		yahclikBandwidth: [],
		pingHighLatencyYahclickNG: [],
		yahclikBandwidthNG: [],
	},
	vouchers: {
		loading: false,
		hasVouchers: false,
		vouchersData: [],
		dailyClients: {
			loadingClients: false,
			data: [],
		},
		pagination: {
			page: 1,
			perPage: 10,
			totalItems: 0,
			totalPage: 0,
		},
	},
	users: {
		loading: false,
		data: null,
		pagination: {
			page: 1,
			perPage: 10,
			totalItems: 0,
			totalPage: 0,
		},
	},
	voucherTokens: {
		loading: false,
		vouchersTokenData: [],
		pagination: {
			page: 1,
			perPage: 10,
			totalItems: 0,
			totalPage: 0,
		},
	},
};

const typeOrder = [
	"signalobstruction",
	"obstruction",
	"downlink",
	"uplink",
	"pinglatency",
	"pingdrop",
];
const getTypeIndex = (type) => typeOrder.indexOf(type);

const vesselsSlice = createSlice({
	name: "auth/user",
	initialState,
	reducers: {
		setVesselsData: (state, action) => {
			state.data = action.payload.data;
			state.pagination.totalItems = action.payload.totalItems;
			state.pagination.page = action.payload.page;
			state.pagination.perPage = action.payload.perPage;
			state.pagination.totalPage = Math.ceil(action.payload.totalItems / action.payload.perPage);
			state.activation.selectedKit = {
				id: null,
				name: null,
				data: null,
				loading: false,
			};
		},
		setLoadingVesssels: (state, action) => {
			state.loading = action.payload;
		},
		setSelectedVessel: (state, action) => {
			state.selectedVessel = action.payload;
			state.activation.selectedKit = {
				id: null,
				name: null,
				data: null,
				loading: false,
			};
		},
		setAllVesselsListMore: (state, action) => {
			const data = action.payload;
			state.list = data || [];
		},
		setVesselDetails: (state, action) => {
			state.vesselDetails = action.payload;
			if (!state.intelsat) {
				state.intelsat = { loading: false, hasIntelsat: false, data: [] };
			}

			state.starlink.hasStarlink = action.payload !== null && action.payload?.starlinks?.length > 0;
			state.intelsat.hasIntelsat = action.payload !== null && action.payload?.intelsats?.length > 0;

			state.peplink.hasPeplink =
				action.payload !== null &&
				action.payload?.smartboxes?.some((box) => box.smartbox?.name === "Peplink");
			state.starlink.starlinkTelemetry = [];
			state.intelsat.data = [];

			if (action.payload?.starlinks?.length > 0) {
				action.payload.starlinks.forEach((item) => {
					const telemetryEntries = [
						{
							id: item.id,
							type: "signalobstruction",
							label: "Starlink Signal Quality " + item.service_line_number,
							option:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							range:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							device_id: item?.device_id,
							service_line_number: item?.service_line_number,
							telemetry_endpoint: item?.acccount_number?.telemetry_endpoint,
							acccount_number: item?.acccount_number,
							panelId: 22,
							grafanaUrl: `https://grafanaiec.oneopticloud.com/d-solo/isFoa0z7k/grafana-metrics?from=now-${
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h"
							}&to=now&var-terminal_id=ut${item?.device_id}&var-ds=${
								item?.acccount_number?.datasource
							}&var-job_name=${item?.acccount_number?.telemetry_endpoint}&refresh=10s&orgId=1&theme=dark&panelId=22&__feature.dashboardSceneSolo`,

							data: null,
						},
						{
							id: item.id,
							type: "obstruction",
							label: "Starlink Obstruction " + item.service_line_number,
							option:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							range:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							device_id: item?.device_id,
							service_line_number: item?.service_line_number,
							telemetry_endpoint: item?.acccount_number?.telemetry_endpoint,
							acccount_number: item?.acccount_number,
							panelId: 20,
							grafanaUrl: `https://grafanaiec.oneopticloud.com/d-solo/isFoa0z7k/grafana-metrics?from=now-${
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h"
							}&to=now&var-terminal_id=ut${item?.device_id}&var-ds=${item?.acccount_number?.datasource}&var-job_name=${item?.acccount_number?.telemetry_endpoint}&refresh=10s&orgId=1&theme=dark&panelId=20&__feature.dashboardSceneSolo`,
							data: null,
						},
						{
							id: item.id,
							type: "downlink",
							label: "Starlink Downlink Throughput " + item.service_line_number,
							option:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							range:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							device_id: item?.device_id,
							service_line_number: item?.service_line_number,
							telemetry_endpoint: item?.acccount_number?.telemetry_endpoint,
							acccount_number: item?.acccount_number,
							panelId: 19,
							grafanaUrl: `https://grafanaiec.oneopticloud.com/d-solo/isFoa0z7k/grafana-metrics?from=now-${
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h"
							}&to=now&var-terminal_id=ut${item?.device_id}&var-ds=${item?.acccount_number?.datasource}&var-job_name=${item?.acccount_number?.telemetry_endpoint}&refresh=10s&orgId=1&theme=dark&panelId=19&__feature.dashboardSceneSolo`,
							data: null,
						},
						{
							id: item.id,
							type: "uplink",
							label: "Starlink Uplink Throughput" + item.service_line_number,
							option:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							range:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							device_id: item?.device_id,
							service_line_number: item?.service_line_number,
							telemetry_endpoint: item?.acccount_number?.telemetry_endpoint,
							acccount_number: item?.acccount_number,
							panelId: 25,
							grafanaUrl: `https://grafanaiec.oneopticloud.com/d-solo/isFoa0z7k/grafana-metrics?from=now-${
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h"
							}&to=now&var-terminal_id=ut${item?.device_id}&var-ds=${item?.acccount_number?.datasource}&var-job_name=${item?.acccount_number?.telemetry_endpoint}&refresh=10s&orgId=1&panelId=25&__feature.dashboardSceneSolo`,
							data: null,
						},
						{
							id: item.id,
							type: "pinglatency",
							label: "Starlink Latency Throughput " + item.service_line_number,
							option:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							range:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							device_id: item?.device_id,
							service_line_number: item?.service_line_number,
							telemetry_endpoint: item?.acccount_number?.telemetry_endpoint,
							acccount_number: item?.acccount_number,
							panelId: 23,
							grafanaUrl: `https://grafanaiec.oneopticloud.com/d-solo/isFoa0z7k/grafana-metrics?from=now-${
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h"
							}&to=now&var-terminal_id=ut${item?.device_id}&var-ds=${item?.acccount_number?.datasource}&var-job_name=${item?.acccount_number?.telemetry_endpoint}&refresh=10s&orgId=1&theme=dark&panelId=23&__feature.dashboardSceneSolo`,
							data: null,
						},

						{
							id: item.id,
							type: "pingdrop",
							label: "Starlink Ping Drop Rate " + item.service_line_number,
							option:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							range:
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h",
							device_id: item?.device_id,
							service_line_number: item?.service_line_number,
							telemetry_endpoint: item?.acccount_number?.telemetry_endpoint,
							acccount_number: item?.acccount_number,
							panelId: 24,
							grafanaUrl: `https://grafanaiec.oneopticloud.com/d-solo/isFoa0z7k/grafana-metrics?from=now-${
								item?.acccount_number?.telemetry_endpoint === "ItalyPool_telemetry_endpoint"
									? "1h"
									: "3h"
							}&to=now&var-terminal_id=ut${item?.device_id}&var-ds=${item?.acccount_number?.datasource}&var-job_name=${item?.acccount_number?.telemetry_endpoint}&refresh=10s&orgId=1&theme=dark&panelId=24&__feature.dashboardSceneSolo`,
							data: null,
						},
					];

					state.starlink.starlinkTelemetry.push(...telemetryEntries);
				});
			}

			if (action.payload?.intelsats?.length > 0) {
				state.intelsat.data.push(...action.payload.intelsats);
			}
		},
		setVesselPhoto: (state, action) => {
			if (state.vesselDetails && "photo" in state.vesselDetails) {
				state.vesselDetails.photo = action.payload;
			} else if (state.vesselDetails) {
				state.vesselDetails = {
					...state.vesselDetails,
					photo: action.payload,
				};
			}
		},
		setLoadingVesselDetails: (state, action) => {
			state.starlink.loadingVesselDetails = action.payload;
		},

		setStarlinkUsage: (state, action) => {
			const {
				starlinkId,
				data,
				kit_numberr,
				service_line_number,
				usage_this_month,
				usage_previous_month,
			} = action.payload;
			const existingIndex = state.starlink.starlinkUsage.findIndex(
				(item) => item.id === starlinkId,
			);
			if (existingIndex !== -1) {
				state.starlink.starlinkUsage[existingIndex].data = data;
			} else {
				state.starlink.starlinkUsage.push({
					id: starlinkId,
					kit_number: kit_numberr,
					service_line_number: service_line_number,
					usage_this_month: usage_this_month,
					usage_previous_month: usage_previous_month,
					data: data,
				});
			}
		},

		setStarlinkAlerts: (state, action) => {
			const { data } = action.payload;
			state.starlink.starlinkAlerts = data;
		},

		setStarlinkTelemetry: (state, action) => {
			const { starlinkId, type, label, kit_number, option, device_id, data } = action.payload;

			const existingIndex = state.starlink.starlinkTelemetry.findIndex(
				(item) => item.id === starlinkId && item.type === type,
			);

			if (existingIndex !== -1) {
				state.starlink.starlinkTelemetry[existingIndex] = {
					...state.starlink.starlinkTelemetry[existingIndex],
					data: data,
					label: label + " " + kit_number,
					option: option,
					device_id: device_id,
				};
			} else {
				state.starlink.starlinkTelemetry.push({
					id: starlinkId,
					type: type,
					label: label + " " + kit_number,
					option: option,
					device_id: device_id,
					data: data,
				});
			}

			// Sort the telemetry array according to typeOrder
			state.starlink.starlinkTelemetry.sort((a, b) => getTypeIndex(a.type) - getTypeIndex(b.type));
		},

		setLoadingVesselDetailsStarlink: (state, action) => {
			// if (!state.starlink) {
			//   state.starlink = {};
			// }
			state.starlink.loadingVesselDetailsStarlink = action.payload;
		},

		setClearVessel: (state, action) => {
			state.starlink.starlinkUsage = [];
			state.starlink.starlinkTelemetry = [];
			state.vesselDetails = null;
			state.selectedVessel = null;
		},
		setClearStarlinkTab: (state, action) => {
			state.starlink.starlinkUsage = [];
			// state.starlink.starlinkTelemetry = [];
			state.starlink.starlinkAlerts = [];
		},
		setGraphTelemetry: (state, action) => {
			const { id, type, data } = action.payload;
			const idAsString = String(id);

			// const existingIndex = state.starlink.starlinkTelemetry.findIndex(item => item.type === String(type) && item.id === String(id));
			const foundItem = state.starlink.starlinkTelemetry.findIndex(
				(item) => item.type === type && String(item.id) === idAsString,
			);
			if (foundItem !== -1) {
				state.starlink.starlinkTelemetry[foundItem].data = data;
			}
		},
		setGraphTelemetryUrl: (state, action) => {
			const { id, type, range } = action.payload;
			const idAsString = String(id);

			// const existingIndex = state.starlink.starlinkTelemetry.findIndex(item => item.type === String(type) && item.id === String(id));
			const foundItem = state.starlink.starlinkTelemetry.findIndex(
				(item) => item.type === type && String(item.id) === idAsString,
			);
			console.error("foundItem", foundItem);
			if (foundItem !== -1) {
				state.starlink.starlinkTelemetry[foundItem].range = range;
				state.starlink.starlinkTelemetry[foundItem].grafanaUrl =
					`https://grafanaiec.oneopticloud.com/d-solo/isFoa0z7k/grafana-metrics?from=now-${range}&to=now&var-terminal_id=ut${state.starlink.starlinkTelemetry[foundItem].device_id}&var-ds=${state.starlink.starlinkTelemetry[foundItem].acccount_number?.datasource}&var-job_name=${state.starlink.starlinkTelemetry[foundItem].telemetry_endpoint}&refresh=10s&orgId=1&theme=dark&panelId=${state.starlink.starlinkTelemetry[foundItem].panelId}&__feature.dashboardSceneSolo`;
			} else {
				console.error("Item not found.");
			}
		},
		setVesselKits: (state, action) => {
			state.activation.kits.data = action.payload.data;
			state.activation.kits.pagination.totalItems = action.payload.totalItems;
			state.activation.kits.pagination.page = action.payload.page;
			state.activation.kits.pagination.perPage = action.payload.perPage;
			state.activation.kits.pagination.totalPage = Math.ceil(
				action.payload.totalItems / action.payload.perPage,
			);
			state.activation.selectedKit = {
				id: null,
				name: null,
				data: null,
				loading: false,
			};
		},
		setLoadingVesselKits: (state, action) => {
			state.activation.kits.loadingkits = action.payload;
		},
		setSelectedKitLoading(state, action) {
			state.activation.selectedKit.loading = action.payload;
		},
		setSelectedKitName(state, action) {
			state.activation.selectedKit = {
				id: action.payload.id,
				name: action.payload.name,
				data: null,
				loading: true,
			};
		},
		setSelectedKit(state, action) {
			state.activation.selectedKit = {
				...state.activation.selectedKit, // Spread existing id and name
				data: action.payload,
				loading: false, // Automatically set loading to false when kit is set
			};
		},

		setRemovedKit: (state, action) => {
			state.activation.selectedKit = {
				id: null,
				name: null,
				data: null,
				loading: false,
			};
		},

		setLoadingPeplink(state, action) {
			state.peplink.loading = action.payload;
		},

		setPeplinkWan: (state, action) => {
			let { VesselId, hours, type, data } = action.payload;
			if (!state.peplink) {
				state.peplink = {
					loading: false,
					hasPeplink: false,
					wan1: [],
					wan2: [],
					cellular: [],
					quality: [],
				};
			}
			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}

			const existingIndex = state.peplink.wan1.findIndex((item) => item.id === VesselId);

			if (existingIndex !== -1) {
				state.peplink.wan1[existingIndex].data = data;
				state.peplink.wan1[existingIndex].hours = hours;
			} else {
				state.peplink.wan1.push({
					id: VesselId,
					hours: hours,
					type: type,
					data: data,
				});
			}
		},

		setPeplinkWan2: (state, action) => {
			let { VesselId, hours, type, data } = action.payload;
			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}
			const existingIndex = state.peplink.wan2.findIndex((item) => item.id === VesselId);

			if (existingIndex !== -1) {
				state.peplink.wan2[existingIndex].data = data;
				state.peplink.wan2[existingIndex].hours = hours;
			} else {
				state.peplink.wan2.push({
					id: VesselId,
					hours: hours,
					type: type,
					data: data,
				});
			}
		},

		setPeplinkCellular: (state, action) => {
			let { VesselId, hours, kit_number, type, data } = action.payload;

			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}

			const existingIndex = state.peplink.cellular.findIndex((item) => item.id === VesselId);

			if (existingIndex !== -1) {
				state.peplink.cellular[existingIndex].data = data;
				state.peplink.cellular[existingIndex].hours = hours;
			} else {
				state.peplink.cellular.push({
					id: VesselId,
					hours: hours,
					type: type,
					data: data,
				});
			}
		},

		setPeplinkQuality: (state, action) => {
			let { VesselId, hours, type, data } = action.payload;
			// Find the index of the object with matching VesselId, if it exists
			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}
			const existingIndex = state.peplink.quality.findIndex((item) => item.id === VesselId);
			if (existingIndex !== -1) {
				// If exists, update the data
				state.peplink.quality[existingIndex].data = data;
				state.peplink.quality[existingIndex].hours = hours;
			} else {
				state.peplink.quality.push({
					id: VesselId,
					hours: hours,
					type: type,
					data: data,
				});
			}
		},

		setWansStatus: (state, action) => {
			const { data } = action.payload;

			state.peplink.status = data;
		},

		setClearPeplink: (state, action) => {
			state.peplink.wan1 = [];
			state.peplink.wan2 = [];
			state.peplink.cellular = [];
			state.peplink.quality = [];
			state.peplink.status = [];
			state.peplink.wan1.stats = null;
			state.peplink.wan2.stats = null;
			state.peplink.cellular.stats = null;
		},

		setPeplinkStats: (state, action) => {
			const { usageCellular } = action.payload;

			state.peplink.cellular.stats = usageCellular;
		},
		setListModule: (state, action) => {
			state.listModules = action.payload;
		},

		resetState(state) {
			return initialState;
		},
		setAllVesselList: (state, action) => {
			const data = action.payload;
			state.list = data || [];
		},
		setPeplinkSubscription: (state, action) => {
			if (!state.peplink.subscription || state.peplink.subscription) {
				state.peplink.subscription = action.payload;
			}
		},
		setDeletedKit: (state, action) => {
			state.activation.kits = state.activation.kits.filter((kit) => kit.id !== action.payload);
		},

		setEditKit: (state, action) => {
			const { id, updatedData } = action.payload;
			state.activation.kits = state.activation.kits.map((unit) =>
				unit.id === id ? { ...unit, ...updatedData } : unit,
			);
		},

		setPeplinkOraganization: (state, action) => {
			if (!state.peplink.oraganization || state.peplink.oraganization) {
				state.peplink.oraganization = action.payload;
			}
		},

		setPeplinkGroup: (state, action) => {
			if (!state.peplink.group || state.peplink.group) {
				state.peplink.group = action.payload;
			}
		},

		setLoadingFortigate(state, action) {
			state.fortigate.loading = action.payload;
		},

		setPingHighLatency: (state, action) => {
			let { VesselId, hours, type, data } = action.payload;

			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}

			const existingIndex = state.fortigate.pingHighLatency.findIndex(
				(item) => item.id === VesselId,
			);

			const newEntry = {
				id: VesselId,
				hours: hours,
				type: type,
				data: data,
			};

			if (existingIndex !== -1) {
				state.fortigate.pingHighLatency[existingIndex].data = data;
			} else {
				state.fortigate.pingHighLatency.push(newEntry);
			}
		},

		setSTARLINKCorporate: (state, action) => {
			let { VesselId, hours, type, data } = action.payload;

			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}

			const existingIndex = state.fortigate.starlinkFortigateInterfaceCorporate.findIndex(
				(item) => item.id === VesselId,
			);

			const newEntry = {
				id: VesselId,
				hours: hours,
				type: type,
				data: data,
			};

			if (existingIndex !== -1) {
				state.fortigate.starlinkFortigateInterfaceCorporate[existingIndex].data = data;
			} else {
				state.fortigate.starlinkFortigateInterfaceCorporate.push(newEntry);
			}
		},

		setSTARLINKWelfare: (state, action) => {
			let { VesselId, hours, type, data } = action.payload;

			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}

			const existingIndex = state.fortigate.starlinkFortigateInterfaceWelfare.findIndex(
				(item) => item.id === VesselId,
			);

			const newEntry = {
				id: VesselId,
				hours: hours,
				type: type,
				data: data,
			};

			if (existingIndex !== -1) {
				state.fortigate.starlinkFortigateInterfaceWelfare[existingIndex].data = data;
			} else {
				state.fortigate.starlinkFortigateInterfaceWelfare.push(newEntry);
			}
		},

		setSTARLINKWAN1: (state, action) => {
			let { VesselId, hours, type, data } = action.payload;

			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}

			const existingIndex = state.fortigate.starlinkFortigateInterfaceWAN1.findIndex(
				(item) => item.id === VesselId,
			);

			const newEntry = {
				id: VesselId,
				hours: hours,
				type: type,
				data: data,
			};

			if (existingIndex !== -1) {
				state.fortigate.starlinkFortigateInterfaceWAN1[existingIndex].data = data;
			} else {
				state.fortigate.starlinkFortigateInterfaceWAN1.push(newEntry);
			}
		},

		setSTARLINKWAN2: (state, action) => {
			let { VesselId, hours, type, data } = action.payload;

			if (typeof VesselId === "string" && !isNaN(VesselId)) {
				VesselId = parseInt(VesselId, 10);
			}

			const existingIndex = state.fortigate.starlinkFortigateInterfaceWAN2.findIndex(
				(item) => item.id === VesselId,
			);

			const newEntry = {
				id: VesselId,
				hours: hours,
				type: type,
				data: data,
			};

			if (existingIndex !== -1) {
				state.fortigate.starlinkFortigateInterfaceWAN2[existingIndex].data = data;
			} else {
				state.fortigate.starlinkFortigateInterfaceWAN2.push(newEntry);
			}
		},

		setClearFortigate: (state, action) => {
			if (!state.fortigate) {
				state.fortigate = {
					pingHighLatency: [],
					starlinkFortigateInterfaceCorporate: [],
					starlinkFortigateInterfaceWelfare: [],
					starlinkFortigateInterfaceWAN1: [],
					starlinkFortigateInterfaceWAN2: [],
				};
			} else {
				state.fortigate.pingHighLatency = [];
				state.fortigate.starlinkFortigateInterfaceCorporate = [];
				state.fortigate.starlinkFortigateInterfaceWelfare = [];
				state.fortigate.starlinkFortigateInterfaceWAN1 = [];
				state.fortigate.starlinkFortigateInterfaceWAN2 = [];
			}
		},
		loadingCybersecurity: (state) => {
			state.cybersecurity.loading = true;
		},
		resetCybersecurity: (state) => {
			state.cybersecurity.loading = false;
			state.cybersecurity.hasCybersecurity = false;
			state.cybersecurity.data = null;
		},
		setCybersecurityData: (state, action) => {
			state.cybersecurity.loading = false;
			state.cybersecurity.hasCybersecurity = true;
			state.cybersecurity.data = action.payload;
		},

		LoadingYahclick(state, action) {
			state.yahclick.loading = action.payload;
		},
		setYahclickData(state, action) {
			state.yahclick.loading = false;
			state.yahclick.hasYahclick = true;
			state.yahclick.yahclickData = action.payload;
		},
		setPingHighLatencyYahclick(state, action) {
			let { YahclickId, hours, type, data } = action.payload;

			if (typeof YahclickId === "string" && !isNaN(YahclickId)) {
				YahclickId = parseInt(YahclickId, 10);
			}

			const existingIndex = state.yahclick.pingHighLatencyYahclick.findIndex(
				(item) => item.id === YahclickId,
			);

			const newEntry = { id: YahclickId, hours, type, data };

			if (existingIndex !== -1) {
				state.yahclick.pingHighLatencyYahclick[existingIndex].data = data;
			} else {
				state.yahclick.pingHighLatencyYahclick.push(newEntry);
			}
		},
		setYahclickBandwidth: (state, action) => {
			let { YahclickId, hours, type, data } = action.payload;

			if (typeof YahclickId === "string" && !isNaN(YahclickId)) {
				YahclickId = parseInt(YahclickId, 10);
			}

			const existingIndex = state.yahclick.yahclikBandwidth.findIndex(
				(item) => item.id === YahclickId,
			);

			const newEntry = {
				id: YahclickId,
				hours: hours,
				type: type,
				data: data,
			};

			if (existingIndex !== -1) {
				state.yahclick.yahclikBandwidth[existingIndex].data = data;
			} else {
				state.yahclick.yahclikBandwidth.push(newEntry);
			}
		},
		setPingHighLatencyYahclickNG(state, action) {
			let { YahclickId, hours, type, data } = action.payload;

			if (typeof YahclickId === "string" && !isNaN(YahclickId)) {
				YahclickId = parseInt(YahclickId, 10);
			}

			const existingIndex = state.yahclick.pingHighLatencyYahclickNG.findIndex(
				(item) => item.id === YahclickId,
			);

			const newEntry = { id: YahclickId, hours, type, data };

			if (existingIndex !== -1) {
				state.yahclick.pingHighLatencyYahclickNG[existingIndex].data = data;
			} else {
				state.yahclick.pingHighLatencyYahclickNG.push(newEntry);
			}
		},
		setYahclickBandwidthNG: (state, action) => {
			let { YahclickId, hours, type, data } = action.payload;

			if (typeof YahclickId === "string" && !isNaN(YahclickId)) {
				YahclickId = parseInt(YahclickId, 10);
			}

			const existingIndex = state.yahclick.yahclikBandwidthNG.findIndex(
				(item) => item.id === YahclickId,
			);

			const newEntry = {
				id: YahclickId,
				hours: hours,
				type: type,
				data: data,
			};

			if (existingIndex !== -1) {
				state.yahclick.yahclikBandwidthNG[existingIndex].data = data;
			} else {
				state.yahclick.yahclikBandwidthNG.push(newEntry);
			}
		},
		setClearYahclick: (state, action) => {
			if (!state.yahclick) {
				state.yahclick = {
					pingHighLatencyYahclick: [],
					yahclikBandwidth: [],
					pingHighLatencyYahclickNG: [],
					yahclikBandwidthNG: [],
				};
			} else {
				state.yahclick.pingHighLatencyYahclick = [];
				state.yahclick.yahclikBandwidth = [];
				state.yahclick.pingHighLatencyYahclickNG = [];
				state.yahclick.yahclikBandwidthNG = [];
			}
		},
		setLoadingVouchers(state, action) {
			state.vouchers.loading = action.payload;
		},

		setVouchersData(state, action) {
			const { data, page, perPage, totalItems } = action.payload;

			state.vouchers.loading = false;
			state.vouchers.hasVouchers = data.length > 0;
			state.vouchers.data = data;
			state.vouchers.pagination = {
				page,
				perPage,
				totalItems,
				totalPage: Math.ceil(totalItems / perPage),
			};
		},
		setVesselUsers: (state, action) => {
			const { data, page, perPage, totalItems } = action.payload;

			state.users.loading = false;
			state.users.data = data;
			state.users.pagination = {
				page,
				perPage,
				totalItems,
				totalPage: Math.ceil(totalItems / perPage),
			};
		},
		setLoadingVesselUsers: (state, action) => {
			state.users.loading = action.payload;
		},

		// Actions for daily clients
		setLoadingDailyClientCount(state, action) {
			state.vouchers.dailyClients.loadingClients = action.payload;
		},
		setDailyClientCountData(state, action) {
			const data = action.payload;

			if (data) {
				state.vouchers.dailyClients.loadingClients = false;
				state.vouchers.dailyClients.data = data;
			}
		},

		setkitHistoryData: (state, action) => {
			const { data, page, perPage, totalItems } = action.payload;

			state.activation.kits.history.loading = false;
			state.activation.kits.history.data = data;
			state.activation.kits.history.pagination = {
				page,
				perPage,
				totalItems,
				totalPage: Math.ceil(totalItems / perPage),
			};
		},
		setLoadingkitHistory: (state, action) => {
			state.activation.kits.history.loading = action.payload;
		},

		setLoadingVoucherTokens(state, action) {
			state.voucherTokens.loading = action.payload;
		},

		setVoucherTokensData(state, action) {
			const { data, page, perPage, totalItems } = action.payload;

			state.voucherTokens.loading = false;
			state.voucherTokens.data = data;
			state.voucherTokens.pagination = {
				page,
				perPage,
				totalItems,
				totalPage: Math.ceil(totalItems / perPage),
			};
		},

		toggleVesselsFiltersVisibility: toggleReduxSliceFiltersVisibility,
	},
	extraReducers: (builder) => {
		builder.addCase(REHYDRATE, (state, action) => {
			// Get persisted filters
			const persistedFilters = action.payload?.modules?.vessels?.filters ?? null;

			// Merge filters with persisted state
			state.filters = mergeFilters(persistedFilters, defaultFilters);
		});
	},
});

export const {
	setVesselsData,
	setLoadingVesssels,
	setSelectedVessel,
	setAllVesselsListMore,
	setVesselDetails,
	setVesselPhoto,
	setLoadingVesselDetails,
	setStarlinkUsage,
	setStarlinkTelemetry,
	setClearVessel,
	setLoadingVesselDetailsStarlink,
	setGraphTelemetry,
	setStarlinkAlerts,
	setVesselKits,
	setLoadingVesselKits,

	setSelectedKitLoading,
	setSelectedKitName,
	setSelectedKit,
	setLoadingPeplink,
	setPeplinkWan,
	setPeplinkWan2,
	setRemovedKit,
	setPeplinkCellular,
	setPeplinkQuality,
	setClearStarlinkTab,
	setClearPeplink,
	setWansStatus,
	setPeplinkUsage,
	setListModule,
	setAllVesselList,
	setPeplinkSubscription,

	setGraphTelemetryUrl,
	setDeletedKit,
	setEditKit,
	setPeplinkOraganization,
	setPeplinkGroup,
	setLoadingFortigate,
	setPingHighLatency,
	setSTARLINKCorporate,
	setSTARLINKWelfare,
	setSTARLINKWAN1,
	setSTARLINKWAN2,
	setClearFortigate,

	loadingCybersecurity,
	resetCybersecurity,
	setCybersecurityData,
	LoadingYahclick,
	setYahclickData,
	setPingHighLatencyYahclick,
	setYahclickBandwidth,
	setPingHighLatencyYahclickNG,
	setYahclickBandwidthNG,
	setClearYahclick,
	setLoadingVouchers,
	setVouchersData,
	setVesselUsers,
	setLoadingVesselUsers,
	setLoadingDailyClientCount,
	setDailyClientCountData,
	setkitHistoryData,
	setLoadingkitHistory,
	setLoadingVoucherTokens,
	setVoucherTokensData,

	toggleVesselsFiltersVisibility,
} = vesselsSlice.actions;
export default vesselsSlice.reducer;
